import './styles/index.css';
import { renderNavigation } from './components/navigation.js';
import { renderGettingStartedView } from './components/getting-started-view.js';
import { renderOverviewView } from './components/overview-view.js';
import { renderAssessmentView } from './components/assessment-view.js';
import { renderDemographicsView } from './components/demographics-view.js';
import { renderStrategyView } from './components/strategy-view.js';
import { renderConceptsView } from './components/concepts-view.js';
import { renderComparisonView } from './components/comparison-view.js';
import { renderBallotView } from './components/ballot-view.js';
import { renderProfileModal } from './components/profile-modal.js';
import { renderReviewTrackerBanner } from './components/inline-feedback.js';
import { conceptsData } from './data/concepts-data.js';
import { feedbackStore } from './data/feedback-store.js';

class App {
  constructor() {
    this.currentTab = 'getting-started';
    this.activeConceptId = 'concept-1';
    this.activeSubTab = 'collateral';
    this.draggedRankIndex = null;
    this.init();
  }

  init() {
    this.render();
    this.attachEventListeners();
    
    // Subscribe to store updates to keep UI synchronized
    feedbackStore.subscribe(() => {
      this.updateHeaderPillAndBanner();
    });
  }

  setTab(tabId) {
    this.currentTab = tabId;
    this.render();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  setConcept(conceptId) {
    this.activeConceptId = conceptId;
    this.render();
  }

  setSubTab(subTabId) {
    this.activeSubTab = subTabId;
    this.render();
  }

  applyTheme(conceptId) {
    const concept = conceptsData.find(c => c.id === conceptId);
    if (!concept || !concept.cssTokens) return;

    const root = document.documentElement;
    Object.entries(concept.cssTokens).forEach(([prop, val]) => {
      root.style.setProperty(prop, val);
    });

    const btn = document.getElementById('btn-apply-theme');
    if (btn) {
      btn.innerHTML = `✓ Applied ${concept.name} Theme!`;
      btn.style.background = concept.colors[0].hex;
      setTimeout(() => {
        if (btn) btn.innerHTML = `<span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ${concept.colors[0].hex}; margin-right: 6px;"></span>Apply ${concept.name} Theme to UI`;
      }, 2500);
    }
  }

  // WCAG 2.1 Contrast Calculation Utilities
  hexToRgb(hex) {
    hex = hex.replace(/^#/, '');
    if (hex.length === 3) {
      hex = hex.split('').map(c => c + c).join('');
    }
    const num = parseInt(hex, 16);
    return {
      r: (num >> 16) & 255,
      g: (num >> 8) & 255,
      b: num & 255
    };
  }

  getLuminance(hex) {
    const { r, g, b } = this.hexToRgb(hex);
    const a = [r, g, b].map(v => {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
  }

  calculateContrast(fgHex, bgHex) {
    try {
      const lum1 = this.getLuminance(fgHex);
      const lum2 = this.getLuminance(bgHex);
      const brightest = Math.max(lum1, lum2);
      const darkest = Math.min(lum1, lum2);
      return ((brightest + 0.05) / (darkest + 0.05));
    } catch (e) {
      return 1;
    }
  }

  updateWcagDisplay() {
    const fgSelect = document.getElementById('wcag-fg-select');
    const bgSelect = document.getElementById('wcag-bg-select');
    const previewBox = document.getElementById('wcag-result-preview');
    const ratioNumber = document.getElementById('wcag-ratio-number');
    const badgesBox = document.getElementById('wcag-badges-box');

    if (!fgSelect || !bgSelect || !previewBox || !ratioNumber || !badgesBox) return;

    const fgHex = fgSelect.value;
    const bgHex = bgSelect.value;
    const ratio = this.calculateContrast(fgHex, bgHex);
    const formattedRatio = ratio.toFixed(1) + ' : 1';

    previewBox.style.backgroundColor = bgHex;
    previewBox.style.color = fgHex;
    ratioNumber.textContent = formattedRatio;

    const passAANormal = ratio >= 4.5;
    const passAALarge = ratio >= 3.0;
    const passAAANormal = ratio >= 7.0;
    const passAAALarge = ratio >= 4.5;

    let badgesHtml = '';
    if (passAAANormal) {
      badgesHtml += `<span class="badge badge-success" style="background: #00C853; color: #000; font-weight: 700;">AAA Normal (${formattedRatio})</span>`;
    } else if (passAANormal) {
      badgesHtml += `<span class="badge" style="background: #FFB300; color: #000; font-weight: 700;">AA Normal (${formattedRatio})</span>`;
    } else {
      badgesHtml += `<span class="badge" style="background: #FF3D71; color: #FFF; font-weight: 700;">Fail Normal</span>`;
    }

    if (passAAALarge) {
      badgesHtml += `<span class="badge badge-success" style="background: #00C853; color: #000; font-weight: 700;">AAA Large</span>`;
    } else if (passAALarge) {
      badgesHtml += `<span class="badge" style="background: #FFB300; color: #000; font-weight: 700;">AA Large</span>`;
    } else {
      badgesHtml += `<span class="badge" style="background: #FF3D71; color: #FFF; font-weight: 700;">Fail Large</span>`;
    }

    badgesBox.innerHTML = badgesHtml;
  }

  openLightbox(src, title, desc) {
    const modal = document.getElementById('mockup-lightbox-modal');
    const img = document.getElementById('lightbox-img');
    const titleEl = document.getElementById('lightbox-title');
    const descEl = document.getElementById('lightbox-desc');

    if (!modal || !img || !titleEl || !descEl) return;

    img.src = src;
    titleEl.textContent = title;
    descEl.textContent = desc;
    modal.style.display = 'flex';
  }

  closeLightbox() {
    const modal = document.getElementById('mockup-lightbox-modal');
    if (modal) modal.style.display = 'none';
  }

  openProfileModal() {
    const modal = document.getElementById('profile-modal');
    if (modal) {
      modal.style.display = 'flex';
      const nameInput = document.getElementById('prof-name');
      if (nameInput) setTimeout(() => nameInput.focus(), 100);
    }
  }

  closeProfileModal() {
    const modal = document.getElementById('profile-modal');
    if (modal) modal.style.display = 'none';
  }

  showToast(message) {
    const existing = document.querySelector('.feedback-toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = 'feedback-toast';
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => {
      toast.style.transition = 'opacity 0.4s ease';
      toast.style.opacity = '0';
      setTimeout(() => toast.remove(), 400);
    }, 2800);
  }

  updateHeaderPillAndBanner() {
    const bannerContainer = document.getElementById('review-tracker-banner-container');
    if (bannerContainer) {
      bannerContainer.innerHTML = renderReviewTrackerBanner(this.currentTab);
    }

    const navBadge = document.querySelector('#nav-ballot .nav-badge');
    if (navBadge) {
      const stats = feedbackStore.getProgressStats();
      navBadge.textContent = `${stats.completedCount}/${stats.totalCount}`;
      if (stats.percentage === 100) {
        navBadge.classList.add('badge-success');
      } else {
        navBadge.classList.remove('badge-success');
      }
    }
  }

  render() {
    const appEl = document.getElementById('app');
    if (!appEl) return;

    let mainContent = '';
    switch (this.currentTab) {
      case 'getting-started':
        mainContent = renderGettingStartedView();
        break;
      case 'overview':
        mainContent = renderOverviewView();
        break;
      case 'assessment':
        mainContent = renderAssessmentView();
        break;
      case 'demographics':
        mainContent = renderDemographicsView();
        break;
      case 'strategy':
        mainContent = renderStrategyView();
        break;
      case 'concepts':
        mainContent = renderConceptsView(this.activeConceptId, this.activeSubTab);
        break;
      case 'comparison':
        mainContent = renderComparisonView();
        break;
      case 'ballot':
        mainContent = renderBallotView();
        break;
      default:
        mainContent = renderGettingStartedView();
    }

    appEl.innerHTML = `
      <div class="app-container">
        ${renderNavigation(this.currentTab)}
        <main class="main-viewport">
          ${mainContent}
        </main>
      </div>

      <!-- Persistent Review Tracker Banner -->
      <div id="review-tracker-banner-container">
        ${renderReviewTrackerBanner(this.currentTab)}
      </div>

      <!-- Reviewer Profile Modal -->
      ${renderProfileModal()}
    `;

    this.attachDynamicListeners();
  }

  attachEventListeners() {
    document.addEventListener('click', async (e) => {
      // Main Navigation Tab
      const tabTrigger = e.target.closest('[data-tab]');
      if (tabTrigger) {
        const tab = tabTrigger.getAttribute('data-tab');
        if (tab) this.setTab(tab);
      }

      // Concept Switcher
      const conceptTrigger = e.target.closest('[data-concept]');
      if (conceptTrigger) {
        const concept = conceptTrigger.getAttribute('data-concept');
        if (concept) {
          if (this.currentTab !== 'concepts') {
            this.currentTab = 'concepts';
          }
          this.setConcept(concept);
        }
      }

      // Sub-Tab Switcher inside Concepts View
      const subTabTrigger = e.target.closest('[data-subtab]');
      if (subTabTrigger) {
        const subTab = subTabTrigger.getAttribute('data-subtab');
        if (subTab) this.setSubTab(subTab);
      }

      // Apply Theme Button
      const themeTrigger = e.target.closest('#btn-apply-theme');
      if (themeTrigger) {
        const conceptId = themeTrigger.getAttribute('data-concept-theme');
        if (conceptId) this.applyTheme(conceptId);
      }

      // Logo Canvas Background Switcher
      const logoBgTrigger = e.target.closest('.logo-bg-toggle');
      if (logoBgTrigger) {
        const bg = logoBgTrigger.getAttribute('data-bg');
        const canvas = document.getElementById('logo-preview-canvas');
        if (canvas && bg) {
          canvas.style.backgroundColor = bg;
        }
      }

      // Move Rank Up Button
      const moveUpBtn = e.target.closest('.btn-move-rank-up');
      if (moveUpBtn) {
        const index = parseInt(moveUpBtn.getAttribute('data-rank-index'), 10);
        feedbackStore.moveRankUp(index);
        this.render();
      }

      // Move Rank Down Button
      const moveDownBtn = e.target.closest('.btn-move-rank-down');
      if (moveDownBtn) {
        const index = parseInt(moveDownBtn.getAttribute('data-rank-index'), 10);
        feedbackStore.moveRankDown(index);
        this.render();
      }

      // Mockup Lightbox Open (excluding clicks on micro reaction buttons, drag handles, or rank buttons)
      const isMicroReaction = e.target.closest('.micro-reactions-bar');
      const isRankButton = e.target.closest('.btn-move-rank-up') || e.target.closest('.btn-move-rank-down');
      const mockupCard = e.target.closest('[data-lightbox-src]');
      if (mockupCard && !isMicroReaction && !isRankButton) {
        const src = mockupCard.getAttribute('data-lightbox-src');
        const title = mockupCard.getAttribute('data-lightbox-title');
        const desc = mockupCard.getAttribute('data-lightbox-desc');
        this.openLightbox(src, title, desc);
      }

      // Lightbox Close
      if (e.target.id === 'lightbox-close' || e.target.id === 'lightbox-backdrop') {
        this.closeLightbox();
      }

      // Reviewer Profile Modal Open/Close
      if (
        e.target.closest('#btn-open-profile') || 
        e.target.closest('#btn-banner-set-profile') || 
        e.target.closest('#btn-edit-profile-ballot') ||
        e.target.closest('#btn-prompt-set-profile')
      ) {
        this.openProfileModal();
      }

      if (e.target.id === 'btn-close-profile-modal' || e.target.id === 'btn-cancel-profile' || e.target.id === 'profile-modal-backdrop') {
        this.closeProfileModal();
      }

      // Save Profile Form
      if (e.target.id === 'btn-save-profile') {
        const name = document.getElementById('prof-name')?.value || '';
        const email = document.getElementById('prof-email')?.value || '';
        const role = document.getElementById('prof-role')?.value || '';
        const siteRadio = document.querySelector('input[name="site"]:checked');
        const site = siteRadio ? siteRadio.value : '';

        const validationMsg = document.getElementById('profile-validation-msg');

        if (!name.trim() || !email.trim() || !role || !site) {
          if (validationMsg) validationMsg.style.display = 'block';
          return;
        }

        feedbackStore.updateProfile({ name: name.trim(), email: email.trim(), role, site });
        this.closeProfileModal();
        this.showToast('✓ Profile saved successfully!');
        this.render();
      }

      // Site Radio Card Selection inside Profile Modal
      const siteCard = e.target.closest('.site-radio-card');
      if (siteCard) {
        const radio = siteCard.querySelector('input[type="radio"]');
        if (radio) {
          radio.checked = true;
          document.querySelectorAll('.site-radio-card').forEach(c => c.classList.remove('active'));
          siteCard.classList.add('active');
        }
      }

      // Rubric Score Button Click (1-5 scale)
      const scoreBtn = e.target.closest('[data-score-btn]');
      if (scoreBtn) {
        const score = parseInt(scoreBtn.getAttribute('data-score-btn'), 10);
        const conceptId = scoreBtn.getAttribute('data-concept-id');
        const rubricKey = scoreBtn.getAttribute('data-rubric-key');
        
        if (conceptId && rubricKey && score) {
          feedbackStore.updateConceptScore(conceptId, rubricKey, score);
          
          // Update visual button active state immediately
          const parentRow = scoreBtn.closest('.score-buttons-row');
          if (parentRow) {
            parentRow.querySelectorAll('.score-btn').forEach(btn => btn.classList.remove('active'));
            scoreBtn.classList.add('active');
          }

          // Update header label if present
          const group = scoreBtn.closest('.score-picker-group');
          if (group) {
            const scoreDisplay = group.querySelector('.mono');
            if (scoreDisplay) {
              const labels = { 1: 'Low', 2: 'Modest', 3: 'Moderate', 4: 'Strong', 5: 'Exceptional' };
              scoreDisplay.textContent = `${score}/5 (${labels[score]})`;
              scoreDisplay.style.color = 'var(--accent-primary)';
            }
          }
        }
      }

      // Collateral Micro-Reaction Button Click
      const microBtn = e.target.closest('[data-micro-reaction]');
      if (microBtn) {
        const emoji = microBtn.getAttribute('data-micro-reaction');
        const conceptId = microBtn.getAttribute('data-concept-id');
        const itemId = microBtn.getAttribute('data-item-id');

        if (emoji && conceptId && itemId) {
          feedbackStore.toggleMicroReaction(conceptId, itemId, emoji);
          const wasActive = microBtn.classList.contains('active');
          const bar = microBtn.closest('.micro-reactions-bar');
          if (bar) {
            bar.querySelectorAll('.micro-reaction-btn').forEach(b => b.classList.remove('active'));
            if (!wasActive) microBtn.classList.add('active');
          }
        }
      }

      // Tagline Selection Card in Strategy View
      const taglineCard = e.target.closest('.tagline-option-card');
      if (taglineCard) {
        const radio = taglineCard.querySelector('input[name="taglineChoice"]');
        if (radio) {
          radio.checked = true;
          document.querySelectorAll('.tagline-option-card').forEach(c => c.classList.remove('active'));
          taglineCard.classList.add('active');
          feedbackStore.updateStrategyFeedback({ taglineChoice: radio.value });
        }
      }

      // Priority Collateral Checkbox
      const priorityCard = e.target.closest('.priority-checkbox-card');
      if (priorityCard) {
        const checkbox = priorityCard.querySelector('.priority-collateral-checkbox');
        if (checkbox) {
          const item = checkbox.getAttribute('data-collateral-item');
          checkbox.checked = !checkbox.checked;
          if (checkbox.checked) {
            priorityCard.classList.add('active');
            priorityCard.querySelector('.custom-check-box').textContent = '✓';
          } else {
            priorityCard.classList.remove('active');
            priorityCard.querySelector('.custom-check-box').textContent = '';
          }
          if (item) feedbackStore.togglePriorityCollateral(item);
        }
      }

      // Save & Submit Official Ballot Button
      if (e.target.id === 'btn-submit-ballot') {
        const btn = document.getElementById('btn-submit-ballot');
        if (!feedbackStore.isProfileValid()) {
          this.openProfileModal();
          return;
        }

        try {
          if (btn) {
            btn.innerHTML = '💾 Saving Ballot Locally...';
            btn.disabled = true;
          }

          await feedbackStore.submitFeedback();
          this.showToast('🎉 Official Ballot Saved Locally!');
          this.render();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
          alert(`Submission error: ${err.message}`);
          if (btn) {
            btn.innerHTML = '💾 Submit & Save Official Ballot';
            btn.disabled = false;
          }
        }
      }

      // Copy Markdown Summary to Clipboard
      if (e.target.id === 'btn-copy-markdown-summary' || e.target.id === 'btn-copy-summary') {
        const summary = feedbackStore.generateMarkdownSummary();
        navigator.clipboard.writeText(summary).then(() => {
          this.showToast('📋 Markdown report copied to clipboard!');
        }).catch(() => {
          alert('Could not copy automatically. Please copy the text manually.');
        });
      }

      // Email Ballot to Leadership
      if (e.target.id === 'btn-email-ballot') {
        const mailto = feedbackStore.generateMailtoLink();
        window.location.href = mailto;
      }

      // Reset / Clear Feedback Ballot
      if (e.target.closest('#btn-reset-ballot') || e.target.closest('#btn-reset-all-data')) {
        if (confirm('Are you sure you want to clear this ballot and delete all saved feedback from this browser?')) {
          feedbackStore.resetFeedback();
          this.showToast('🗑️ Submission deleted. Feedback reset to blank state.');
          this.render();
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }

      // WCAG Presets
      const presetBtn = e.target.closest('.wcag-preset-btn');
      if (presetBtn) {
        const fg = presetBtn.getAttribute('data-fg');
        const bg = presetBtn.getAttribute('data-bg');
        const fgSelect = document.getElementById('wcag-fg-select');
        const bgSelect = document.getElementById('wcag-bg-select');
        if (fgSelect && bgSelect && fg && bg) {
          fgSelect.value = fg;
          bgSelect.value = bg;
          this.updateWcagDisplay();
        }
      }
    });

    // Handle Textarea & Input Auto-save
    document.addEventListener('input', (e) => {
      // Concept qualitative textareas
      const conceptTextArea = e.target.closest('[data-concept-text]');
      if (conceptTextArea) {
        const field = conceptTextArea.getAttribute('data-concept-text');
        const conceptId = conceptTextArea.getAttribute('data-concept-id');
        if (field && conceptId) {
          feedbackStore.updateConceptText(conceptId, field, conceptTextArea.value);
        }
      }

      // Strategy custom tagline input
      if (e.target.id === 'strat-custom-tagline') {
        feedbackStore.updateStrategyFeedback({ customTagline: e.target.value });
      }

      // Strategy comments textarea
      if (e.target.id === 'strat-comments') {
        feedbackStore.updateStrategyFeedback({ comments: e.target.value });
      }

      // Ballot advice textarea
      if (e.target.id === 'ballot-advice-input') {
        feedbackStore.updateBallot({ adviceForLeadership: e.target.value });
      }
    });

    // Drag and Drop event listeners for Ranked-Choice Stack
    document.addEventListener('dragstart', (e) => {
      const card = e.target.closest('.draggable-rank-card');
      if (card) {
        this.draggedRankIndex = parseInt(card.getAttribute('data-rank-index'), 10);
        card.classList.add('dragging');
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(this.draggedRankIndex));
      }
    });

    document.addEventListener('dragend', (e) => {
      const card = e.target.closest('.draggable-rank-card');
      if (card) {
        card.classList.remove('dragging');
      }
      document.querySelectorAll('.draggable-rank-card').forEach(c => c.classList.remove('drag-over'));
      this.draggedRankIndex = null;
    });

    document.addEventListener('dragover', (e) => {
      const card = e.target.closest('.draggable-rank-card');
      if (card && this.draggedRankIndex !== null) {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        card.classList.add('drag-over');
      }
    });

    document.addEventListener('dragleave', (e) => {
      const card = e.target.closest('.draggable-rank-card');
      if (card) {
        card.classList.remove('drag-over');
      }
    });

    document.addEventListener('drop', (e) => {
      const card = e.target.closest('.draggable-rank-card');
      if (card && this.draggedRankIndex !== null) {
        e.preventDefault();
        const targetIndex = parseInt(card.getAttribute('data-rank-index'), 10);
        if (this.draggedRankIndex !== targetIndex) {
          feedbackStore.reorderRank(this.draggedRankIndex, targetIndex);
          this.render();
          this.showToast('✓ Ranking updated!');
        }
        card.classList.remove('drag-over');
        this.draggedRankIndex = null;
      }
    });

    document.addEventListener('change', (e) => {
      if (e.target.id === 'wcag-fg-select' || e.target.id === 'wcag-bg-select') {
        this.updateWcagDisplay();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeLightbox();
        this.closeProfileModal();
      }
    });
  }

  attachDynamicListeners() {
    if (this.currentTab === 'concepts' && this.activeSubTab === 'tokens') {
      this.updateWcagDisplay();
    }
  }
}

// Initialize Application
new App();
